import React from 'react';

import { HocWrapper } from '../hoc-wrapper';

export interface HoverableProps {
  hoverIn?: (...args: any[]) => void;
  hoverOut?: (...args: any[]) => void;
}

export interface HoverableState {
  isHovered: boolean;
}

export interface Options extends HoverableProps {
  propName?: string;
  svg?: boolean;
}

const noop = () => undefined;

export default function Hoverable<P extends Options>(
  Component: HocWrapper<P>,
  options?: Options,
): React.ComponentClass<P> {
  const defaultOptions: Options = {
    propName: 'isHovered',
    svg: false,
    hoverIn: noop,
    hoverOut: noop,
  };

  const { propName, svg, hoverIn, hoverOut } = {
    ...defaultOptions,
    ...options,
  } as Options;

  class WrappedComponent extends React.Component<P, HoverableState> {
    public static displayName = '';

    public state: HoverableState = {
      isHovered: false,
    };

    handleHoverIn = () => {
      const { hoverIn: hoverInProp } = this.props as any;
      this.setState({ isHovered: true });
      hoverIn();
      hoverInProp && hoverInProp();
    };

    handleHoverOut = () => {
      const { hoverOut: hoverOutProp } = this.props as any;
      this.setState({ isHovered: false });
      hoverOut();
      hoverOutProp && hoverOutProp();
    };

    public render() {
      const Container = svg ? 'g' : 'div';

      const props = {
        [propName]: this.state.isHovered,
        ...(this.props as any),
      };

      return (
        <Container
          onMouseEnter={this.handleHoverIn}
          onMouseLeave={this.handleHoverOut}
        >
          <Component {...props} />
        </Container>
      );
    }
  }

  const name = Component.displayName || Component.name;
  WrappedComponent.displayName = `Hoverable${name}`;

  return WrappedComponent;
}
